import React from "react";
import {
    Alert,
    Card,
    CardBody,
    Container,
    Row,
    Col,
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Spinner,
    ListGroup,
    ListGroupItem,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Tooltip
} from "reactstrap";
import AsyncSelect from "react-select/async";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import Select from "react-select";
import { RiDeleteBinLine } from "react-icons/ri";
import { FiCopy } from "react-icons/fi";
import { sessionEnded } from "../user/SessionEnded";
import Api from "../../components/helpers/Api";
import SeoNoIndex from "../../components/helpers/SeoNoIndex";

class AdminSongDetail extends React.Component {
    state = {
        isLoading: true,
        isSaving: false,
        isDeleting: false,
        notFound: false,
        id: null,
        title: "",
        alternativeTitle: "",

        // SongBook associations
        associatedSongBooks: [], // [{ id, wsbId, title, shortTitle, songBookSongNumber }]
        associatedSbIds: [],     // [id,...] for multi-select value
        allSongBooks: [],        // full list: [{ id, title, shortTitle }]

        // Musical metadata keyed by wsbId: { keySignature, timeSignature, tempoBpm, meter, ccliNumber }
        songBookMetadata: {},
        savingMetadataId: null,
        metadataSaveSuccessId: null,
        metadataSaveError: null,

        // Theme associations
        associatedThemeIds: [], // [id,...]
        allThemes: [],          // [{ id, title }]

        // Scripture references
        references: [],    // [{ id, bookId, chapter, verseFrom, verseTo }]
        removedRefIds: [], // [id,...]
        allBooks: [],      // [{ id, title, chapterCount, versesCount: { [chapter]: verseCount } }]

        // Author associations
        associatedAuthorIds: [],
        allAuthors: [],

        // Merge feature
        mergeSource: null,
        mergeSearchInput: "",
        showMergeModal: false,
        mergePreview: {
            songBooks: [],        // to add: [{ songBookId, title, shortTitle, songBookSongNumber }]
            themes: [],           // to add: [{ id, title }]
            references: [],       // to add: [{ bookId, chapter, verseFrom, verseTo }]
            alreadySongBooks: [], // from source already associated
            alreadyThemes: [],    // from source already associated
            alreadyReferences: [] // from source already associated
        },
        isMerging: false,

        // Copy Scripture References
        copyRefSearchQuery: "",
        copyRefSearchResults: [],
        copyRefTarget: null,
        copyRefConfirming: false,
        copyingRefs: false,
        copyRefError: null,
        copyRefSuccess: null,

        // Copy tooltip
        copyTooltipOpen: false,

        unauthorized: false
    };

    componentDidMount() {
        try {
            const customer = sessionStorage.getItem("customer");
            const parsed = customer ? JSON.parse(customer) : null;
            console.log("AdminSongDetail customer structure:", parsed);
            if (!parsed?.user?.isAdmin) {
                this.setState({ unauthorized: true });
                return;
            }
            const { id } = this.props.params;
            this.setState({ id }, this.fetchAllData);
        } catch (e) {
            console.error("AdminSongDetail componentDidMount error:", e);
            this.setState({ unauthorized: true });
        }
    }

    // Fetch main song DTO and then associations/lists
    fetchAllData = () => {
        const { id } = this.state;
        Api.get(`/words/${id}`)
            .then((response) => {
                const dto = response.data;
                this.setState({
                    title: dto.title || "",
                    alternativeTitle: dto.alternativeTitle || "",
                    isLoading: false,
                    notFound: false
                });
                // Fetch associations & lookup lists
                this.fetchAssociatedSongBooks(id);
                this.fetchAllSongBooks();
                this.fetchAssociatedThemes(id);
                this.fetchAllThemes();
                this.fetchReferences(id);
                this.fetchAllBooks();
                this.fetchAssociatedAuthors(id);
                this.fetchAllAuthors();
            })
            .catch((error) => {
                if (error.status === 404 || error.response?.status === 404) {
                    this.setState({ notFound: true, isLoading: false });
                } else {
                    console.error("Error fetching song:", error);
                    this.setState({ isLoading: false });
                }
            });
    };

    // --- SongBook fetches and handlers ---

    // Fetch existing associations, including songBookSongNumber and musical metadata
    fetchAssociatedSongBooks = (id) => {
        Api.get(`/words/${id}/words-song-books`)
            .then((response) => {
                const assoc = response.data.map(wsDto => {
                    const sb = wsDto.songBook || {};
                    return {
                        id: sb.id,
                        wsbId: wsDto.id,         // words_song_book row id — used for metadata endpoint
                        title: sb.title,
                        shortTitle: sb.shortTitle,
                        songBookSongNumber: wsDto.songBookSongNumber || ""
                    };
                });
                const ids = assoc.map(x => x.id);

                // Seed metadata state from response
                const songBookMetadata = {};
                response.data.forEach(wsDto => {
                    if (wsDto.id) {
                        songBookMetadata[wsDto.id] = {
                            keySignature:  wsDto.keySignature  || "",
                            timeSignature: wsDto.timeSignature || "",
                            tempoBpm:      wsDto.tempoBpm      != null ? wsDto.tempoBpm : "",
                            meter:         wsDto.meter         || "",
                            ccliNumber:    wsDto.ccliNumber    || ""
                        };
                    }
                });

                this.setState({ associatedSongBooks: assoc, associatedSbIds: ids, songBookMetadata });
            })
            .catch((error) => {
                console.error("Error fetching associated song books:", error);
            });
    };

    // Fetch all song books for multi-select options
    fetchAllSongBooks = () => {
        Api.get(`/song-books`)
            .then((response) => {
                this.setState({ allSongBooks: response.data });
            })
            .catch((error) => {
                console.error("Error fetching all song books:", error);
            });
    };

    // Handler when multi-select changes for song books
    handleSongBooksSelectChange = (selectedOptions) => {
        const newIds = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
        this.setState(prev => {
            const prevAssoc = prev.associatedSongBooks;
            // Determine which were added
            const added = newIds.filter(id => !prev.associatedSbIds.includes(id));
            // Build new associatedSongBooks array
            let assoc = [...prevAssoc];
            // Add newly selected with blank songBookSongNumber
            for (const idToAdd of added) {
                const sbMeta = prev.allSongBooks.find(sb => sb.id === idToAdd) || {};
                assoc.push({
                    id: idToAdd,
                    title: sbMeta.title || "",
                    shortTitle: sbMeta.shortTitle || "",
                    songBookSongNumber: ""
                });
            }
            // Remove those unselected
            assoc = assoc.filter(item => newIds.includes(item.id));
            return {
                associatedSbIds: newIds,
                associatedSongBooks: assoc
            };
        });
    };

    // --- Theme fetches and handlers ---

    fetchAssociatedThemes = (id) => {
        Api.get(`/words/${id}/themes`)
            .then((response) => {
                const ids = response.data.map(th => th.id);
                this.setState({ associatedThemeIds: ids });
            })
            .catch((error) => {
                console.error("Error fetching associated themes:", error);
            });
    };

    fetchAllThemes = () => {
        Api.get(`/themes`)
            .then((response) => {
                this.setState({ allThemes: response.data });
            })
            .catch((error) => {
                console.error("Error fetching all themes:", error);
            });
    };

    handleThemesSelectChange = (selectedOptions) => {
        const ids = selectedOptions ? selectedOptions.map(opt => opt.value) : [];
        this.setState({ associatedThemeIds: ids });
    };

    // --- Scripture references fetch and handlers ---

    fetchReferences = (id) => {
        Api.get(`/words/${id}/references`)
            .then((response) => {
                const refs = response.data.map(r => ({
                    id: r.id,
                    bookId: r.bookId,
                    chapter: r.chapter,
                    verseFrom: r.verseFrom,
                    verseTo: r.verseTo
                }));
                this.setState({ references: refs });
            })
            .catch((error) => {
                console.error("Error fetching references:", error);
            });
    };

    fetchAllBooks = () => {
        Api.get(`/scripture`)
            .then((response) => {
                this.setState({ allBooks: response.data });
            })
            .catch((error) => {
                console.error("Error fetching testament books:", error);
            });
    };

    // 1) Which authors are already attached to this song
    fetchAssociatedAuthors = songId => {
        Api.get(`/words/${songId}/authors`)
            .then(resp => {
                const ids = resp.data.map(a => a.id);
                this.setState({ associatedAuthorIds: ids });
            })
            .catch(console.error);
    };

    // 2) Lookup list of all possible authors
    fetchAllAuthors = () => {
        Api.get(`/authors`)
            .then(resp => this.setState({ allAuthors: resp.data }))
            .catch(console.error);
    };

    // 3) Handle user selecting/unselecting authors
    handleAuthorsSelectChange = selectedOptions => {
        const ids = selectedOptions ? selectedOptions.map(o => o.value) : [];
        this.setState({ associatedAuthorIds: ids });
    };

    handleAddReference = () => {
        this.setState(prev => ({
            references: [
                ...prev.references,
                { id: null, bookId: "", chapter: "", verseFrom: "", verseTo: "" }
            ]
        }));
    };

    handleRemoveReference = (index) => {
        this.setState(prev => {
            const refs = [...prev.references];
            const removedRefIds = [...prev.removedRefIds];
            const ref = refs[index];
            if (ref.id) {
                removedRefIds.push(ref.id);
            }
            refs.splice(index, 1);
            return { references: refs, removedRefIds };
        });
    };

    handleReferenceChange = (index, field, value) => {
        this.setState(prev => {
            const refs = [...prev.references];
            const ref = { ...refs[index] };
            ref[field] = value;
            if (field === "bookId") {
                ref.chapter = "";
                ref.verseFrom = "";
                ref.verseTo = "";
            }
            if (field === "chapter") {
                ref.verseFrom = "";
                ref.verseTo = "";
            }
            if (field === "verseFrom") {
                ref.verseTo = "";
            }
            refs[index] = ref;
            return { references: refs };
        });
    };

    // --- Save / update handlers ---

    handleSave = (e) => {
        e.preventDefault();
        const { id, title, alternativeTitle } = this.state;
        if (!title.trim()) {
            alert("Title cannot be empty.");
            return;
        }
        this.setState({ isSaving: true });

        // 1) Update main fields
        const payload = {
            title: title.trim(),
            alternativeTitle: alternativeTitle.trim()
        };
        Api.put(`/words/${id}`, payload)
            .then(() => {
                // 2) Update associations & references in parallel
                return Promise.all([
                    this.updateSongBookAssociations(id),
                    this.updateThemeAssociations(id),
                    this.updateReferences(id),
                    this.updateAuthorAssociations(id)
                ]);
            })
            .then(() => {
                this.setState({ isSaving: false, removedRefIds: [] }, () => {
                    alert("Song, associations, and references updated successfully.");
                    this.fetchReferences(id);
                });
            })
            .catch((error) => {
                console.error("Error during save:", error);
                this.setState({ isSaving: false });
                alert("Failed to save. See console for details.");
            });
    };

    updateSongBookAssociations = async (wordId) => {
        // Fetch existing associations to know which to add/remove/update
        let existingAssociations = [];
        try {
            const resp = await Api.get(`/words/${wordId}/words-song-books`);
            existingAssociations = resp.data;
        } catch (e) {
            console.error("Error re-fetching existing song books:", e);
        }
        const existingIds = existingAssociations.map(wsDto => wsDto.songBook.id);
        const { associatedSongBooks } = this.state;
        const toAdd = associatedSongBooks.filter(item => !existingIds.includes(item.id));
        const toRemove = existingIds.filter(id => !associatedSongBooks.some(item => item.id === id));
        const toUpdate = associatedSongBooks.filter(item => {
            const existing = existingAssociations.find(wsDto => wsDto.songBook.id === item.id);
            return existing && existing.songBookSongNumber !== item.songBookSongNumber;
        });

        // Add new associations
        for (const item of toAdd) {
            try {
                await Api.post(
                    `/words/${wordId}/song-books/${item.id}`,
                    { songBookSongNumber: item.songBookSongNumber || "" }
                );
            } catch (err) {
                console.error("Add song-book failed:", err);
            }
        }
        // Update changed song numbers
        for (const item of toUpdate) {
            try {
                await Api.patch(
                    `/words/${wordId}/song-books/${item.id}`,
                    { songBookSongNumber: item.songBookSongNumber || "" }
                );
            } catch (err) {
                console.error("Update song-book number failed:", err);
            }
        }
        // Remove old associations
        for (const sbId of toRemove) {
            try {
                await Api.delete(`/words/${wordId}/song-books/${sbId}`);
            } catch (err) {
                console.error("Remove song-book failed:", err);
            }
        }
    };

    updateThemeAssociations = async (wordId) => {
        let existingIds = [];
        try {
            const resp = await Api.get(`/words/${wordId}/themes`);
            existingIds = resp.data.map(th => th.id);
        } catch (e) {
            console.error("Error re-fetching existing themes:", e);
        }
        const { associatedThemeIds } = this.state;
        const toAdd = associatedThemeIds.filter(i => !existingIds.includes(i));
        const toRemove = existingIds.filter(i => !associatedThemeIds.includes(i));

        for (const themeId of toAdd) {
            try { await Api.post(`/words/${wordId}/themes/${themeId}`); }
            catch (err) { console.error(`Add theme ${themeId} failed:`, err); }
        }
        for (const themeId of toRemove) {
            try { await Api.delete(`/words/${wordId}/themes/${themeId}`); }
            catch (err) { console.error(`Remove theme ${themeId} failed:`, err); }
        }
    };

    updateReferences = async (wordId) => {
        const { references, removedRefIds } = this.state;
        // Delete removed
        if (removedRefIds && removedRefIds.length) {
            for (const rid of removedRefIds) {
                try {
                    await Api.delete(`/words/${wordId}/references/${rid}`);
                } catch (err) {
                    console.error(`Failed to delete reference ${rid}:`, err);
                }
            }
        }
        // Update existing
        for (const ref of references) {
            if (ref.id) {
                try {
                    await Api.put(`/words/${wordId}/references/${ref.id}`, {
                        bookId: ref.bookId,
                        chapter: Number(ref.chapter),
                        verseFrom: Number(ref.verseFrom),
                        verseTo: Number(ref.verseTo)
                    });
                } catch (err) {
                    console.error(`Failed to update reference ${ref.id}:`, err);
                }
            }
        }
        // Add new
        for (const ref of references) {
            if (!ref.id) {
                try {
                    await Api.post(`/words/${wordId}/references`, {
                        bookId: ref.bookId,
                        chapter: Number(ref.chapter),
                        verseFrom: Number(ref.verseFrom),
                        verseTo: Number(ref.verseTo)
                    });
                } catch (err) {
                    console.error(`Failed to add reference for book ${ref.bookId}:`, err);
                }
            }
        }
    };

    updateAuthorAssociations = async songId => {
        // fetch existing
        let existing = [];
        try {
            const resp = await Api.get(`/words/${songId}/authors`);
            existing = resp.data.map(a => a.id);
        } catch(e){ console.error(e) }
        const { associatedAuthorIds } = this.state;
        const toAdd    = associatedAuthorIds.filter(id => !existing.includes(id));
        const toRemove = existing.filter(id => !associatedAuthorIds.includes(id));

        // add
        await Promise.all(toAdd.map(id =>
            Api.post(`/words/${songId}/authors/${id}`)
                .catch(err => console.error('Add author failed', err))
        ));
        // remove
        await Promise.all(toRemove.map(id =>
            Api.delete(`/words/${songId}/authors/${id}`)
                .catch(err => console.error('Remove author failed', err))
        ));
    };

    // --- Musical metadata handlers ---

    handleMetadataChange = (wsbId, field, value) => {
        this.setState(prev => ({
            songBookMetadata: {
                ...prev.songBookMetadata,
                [wsbId]: {
                    ...prev.songBookMetadata[wsbId],
                    [field]: value
                }
            }
        }));
    };

    handleSaveMetadata = (wsbId) => {
        const meta = this.state.songBookMetadata[wsbId] || {};
        const tempoBpm = meta.tempoBpm !== "" ? parseInt(meta.tempoBpm, 10) : null;

        if (meta.tempoBpm !== "" && isNaN(tempoBpm)) {
            this.setState({ metadataSaveError: "Tempo must be a number" });
            return;
        }

        this.setState({ savingMetadataId: wsbId, metadataSaveError: null, metadataSaveSuccessId: null });

        Api.put(`/api/admin/words-song-book/${wsbId}/metadata`, {
            keySignature:  meta.keySignature  || null,
            timeSignature: meta.timeSignature || null,
            tempoBpm:      isNaN(tempoBpm) ? null : tempoBpm,
            meter:         meta.meter         || null,
            ccliNumber:    meta.ccliNumber    || null
        })
            .then(() => {
                this.setState({ savingMetadataId: null, metadataSaveSuccessId: wsbId });
                setTimeout(() => this.setState({ metadataSaveSuccessId: null }), 3000);
            })
            .catch(err => {
                this.setState({
                    savingMetadataId: null,
                    metadataSaveError: err.response?.data || "Failed to save metadata"
                });
            });
    };

    handleDelete = () => {
        const { id } = this.state;
        if (!window.confirm("Are you sure you want to delete this song? This cannot be undone.")) {
            return;
        }
        this.setState({ isDeleting: true });
        Api.delete(`/words/${id}`)
            .then(() => {
                this.setState({ isDeleting: false });
                alert("Song deleted.");
                this.props.navigate("/admin/songs");
            })
            .catch((error) => {
                console.error("Error deleting song:", error);
                this.setState({ isDeleting: false });
                alert("Failed to delete. See console for details.");
            });
    };

    handleCancel = () => {
        this.props.navigate("/admin/songs");
    };

    // --- Merge functionality ---

    loadMergeOptions = (inputValue, callback) => {
        const { id } = this.state;
        if (!inputValue || inputValue.length < 1) {
            return callback([]);
        }
        let term = inputValue;
        // strip trailing [id] or (alt) if present
        const bracketIdx = term.indexOf('[');
        if (bracketIdx !== -1) {
            term = term.substring(0, bracketIdx).trim();
        }
        const parenIdx = term.lastIndexOf('(');
        if (parenIdx !== -1 && term.endsWith(')')) {
            term = term.substring(0, parenIdx).trim();
        }
        if (!term) {
            return callback([]);
        }
        Api.get(`/words/paginated?query=${encodeURIComponent(term)}&size=50`)
            .then(response => {
                const options = response.data.content
                    .filter(song => song.id !== id)
                    .map(song => {
                        const alt = song.alternativeTitle ? ` (${song.alternativeTitle})` : "";
                        return {
                            value: song.id,
                            label: `${song.title}${alt} [${song.id}]`
                        };
                    });
                callback(options);
            })
            .catch(err => {
                console.error("Error searching songs for merge:", err);
                callback([]);
            });
    };

    handlePreviewMerge = () => {
        const { mergeSource, id, associatedSbIds, associatedThemeIds, references } = this.state;
        if (!mergeSource) return;
        const sourceId = mergeSource.value;

        Promise.all([
            Api.get(`/words/${sourceId}/words-song-books`),
            Api.get(`/words/${sourceId}/themes`),
            Api.get(`/words/${sourceId}/references`)
        ])
            .then(([wsbResp, thResp, refResp]) => {
                const sourceWsbs = wsbResp.data; // WordsSongBookDto[]
                const sourceThemes = thResp.data; // ThemeDto[]
                const sourceRefs = refResp.data;  // WordsReferenceDto[]

                // SongBooks: already vs preview
                const alreadySongBooks = sourceWsbs
                    .filter(wsDto => associatedSbIds.includes(wsDto.songBook.id))
                    .map(wsDto => ({
                        songBookId: wsDto.songBook.id,
                        title: wsDto.songBook.title,
                        shortTitle: wsDto.songBook.shortTitle,
                        songBookSongNumber: wsDto.songBookSongNumber || ""
                    }));
                const previewSongBooks = sourceWsbs
                    .filter(wsDto => !associatedSbIds.includes(wsDto.songBook.id))
                    .map(wsDto => ({
                        songBookId: wsDto.songBook.id,
                        title: wsDto.songBook.title,
                        shortTitle: wsDto.songBook.shortTitle,
                        songBookSongNumber: wsDto.songBookSongNumber || ""
                    }));

                // Themes
                const alreadyThemes = sourceThemes.filter(th => associatedThemeIds.includes(th.id));
                const previewThemes = sourceThemes.filter(th => !associatedThemeIds.includes(th.id));

                // References
                const alreadyReferences = sourceRefs.filter(srcRef =>
                    references.some(r =>
                        r.bookId === srcRef.bookId &&
                        Number(r.chapter) === srcRef.chapter &&
                        Number(r.verseFrom) === srcRef.verseFrom &&
                        Number(r.verseTo) === srcRef.verseTo
                    )
                ).map(r => ({
                    bookId: r.bookId,
                    chapter: r.chapter,
                    verseFrom: r.verseFrom,
                    verseTo: r.verseTo
                }));
                const previewReferences = sourceRefs.filter(srcRef =>
                    !references.some(r =>
                        r.bookId === srcRef.bookId &&
                        Number(r.chapter) === srcRef.chapter &&
                        Number(r.verseFrom) === srcRef.verseFrom &&
                        Number(r.verseTo) === srcRef.verseTo
                    )
                ).map(r => ({
                    bookId: r.bookId,
                    chapter: r.chapter,
                    verseFrom: r.verseFrom,
                    verseTo: r.verseTo
                }));

                this.setState({
                    mergePreview: {
                        songBooks: previewSongBooks,
                        themes: previewThemes,
                        references: previewReferences,
                        alreadySongBooks,
                        alreadyThemes,
                        alreadyReferences
                    },
                    showMergeModal: true
                });
            })
            .catch(err => {
                console.error("Error fetching source song data for merge:", err);
                alert("Failed to fetch data for preview.");
            });
    };

    handleApplyMerge = () => {
        const { id, mergePreview } = this.state;
        this.setState({ isMerging: true });
        // Add songBooks with number
        const addSongBooks = mergePreview.songBooks.map(sb =>
            Api.post(`/words/${id}/song-books/${sb.songBookId}`, {
                songBookSongNumber: sb.songBookSongNumber || ""
            }).catch(err => {
                console.error("Error merging songBook", sb.songBookId, err);
            })
        );
        // Add themes
        const addThemes = mergePreview.themes.map(th =>
            Api.post(`/words/${id}/themes/${th.id}`).catch(err => {
                console.error("Error merging theme", th.id, err);
            })
        );
        // Add references
        const addRefs = mergePreview.references.map(r =>
            Api.post(`/words/${id}/references`, {
                bookId: r.bookId,
                chapter: r.chapter,
                verseFrom: r.verseFrom,
                verseTo: r.verseTo
            }).catch(err => {
                console.error("Error merging reference", r, err);
            })
        );
        Promise.all([...addSongBooks, ...addThemes, ...addRefs])
            .then(() => {
                this.setState({
                    isMerging: false,
                    showMergeModal: false,
                    mergeSource: null,
                    mergeSearchInput: "",
                    mergePreview: {
                        songBooks: [],
                        themes: [],
                        references: [],
                        alreadySongBooks: [],
                        alreadyThemes: [],
                        alreadyReferences: []
                    }
                }, () => {
                    alert("Merge applied. Reloading data...");
                    this.fetchAllData();
                });
            })
            .catch(err => {
                console.error("Error during merge:", err);
                this.setState({ isMerging: false, showMergeModal: false });
                alert("Merge completed with some errors; check console.");
                this.fetchAllData();
            });
    };

    // Copy title to clipboard + set merge search input
    handleCopyTitle = () => {
        const { title } = this.state;
        if (title) {
            navigator.clipboard.writeText(title).then(() => {
                this.setState({ mergeSearchInput: "" }, () => {
                    this.setState({ mergeSearchInput: title, copyTooltipOpen: true }, () => {
                        setTimeout(() => this.setState({ copyTooltipOpen: false }), 1500);
                    });
                });
            }).catch(err => {
                console.error("Clipboard write failed:", err);
            });
        }
    };

    // --- Copy Scripture References handlers ---

    handleCopyRefSearch = (e) => {
        e.preventDefault();
        const q = this.state.copyRefSearchQuery.trim();
        if (!q) return;
        const { id } = this.state;
        Api.get(`/words/paginated?query=${encodeURIComponent(q)}&size=20`)
            .then(response => {
                const results = (response.data.content || [])
                    .filter(s => s.id !== id)
                    .slice(0, 10);
                this.setState({ copyRefSearchResults: results, copyRefTarget: null, copyRefConfirming: false });
            })
            .catch(err => console.error("Copy ref search failed", err));
    };

    handleSelectCopyRefTarget = (song) => {
        this.setState({ copyRefTarget: song, copyRefConfirming: true, copyRefSearchQuery: "", copyRefSearchResults: [] });
    };

    handleCancelCopyRef = () => {
        this.setState({ copyRefTarget: null, copyRefConfirming: false, copyRefError: null });
    };

    handleConfirmCopyRef = () => {
        const { id, copyRefTarget, references } = this.state;
        this.setState({ copyingRefs: true, copyRefError: null });
        Api.post(`/words/${id}/copy-references-to/${copyRefTarget.id}`)
            .then(res => {
                const added = res.data?.added ?? 0;
                const message = added === 0
                    ? "0 references copied — all already present on target song."
                    : `${added} reference${added !== 1 ? "s" : ""} copied to "${copyRefTarget.title}".`;
                this.setState({
                    copyRefSuccess: message,
                    copyRefConfirming: false,
                    copyRefTarget: null,
                    copyRefError: null,
                    copyingRefs: false
                });
                setTimeout(() => this.setState({ copyRefSuccess: null }), 4000);
            })
            .catch(err => {
                console.error("Copy references failed", err);
                this.setState({
                    copyRefError: err.response?.data?.error || "Copy failed. Please try again.",
                    copyingRefs: false
                });
            });
    };

    render() {
        if (!sessionStorage.getItem("customer")) {
            return sessionEnded();
        }
        if (this.state.unauthorized) return <Navigate to="/" />;
        const {
            isLoading,
            notFound,
            id,
            title,
            alternativeTitle,
            isSaving,
            isDeleting,
            allSongBooks,
            associatedSongBooks,
            associatedSbIds,
            allThemes,
            associatedThemeIds,
            allAuthors,
            associatedAuthorIds,
            references,
            allBooks,
            mergeSource,
            mergeSearchInput,
            showMergeModal,
            mergePreview,
            isMerging,
            copyTooltipOpen,
            songBookMetadata,
            savingMetadataId,
            metadataSaveSuccessId,
            metadataSaveError,
            copyRefSearchQuery,
            copyRefSearchResults,
            copyRefTarget,
            copyRefConfirming,
            copyingRefs,
            copyRefError,
            copyRefSuccess
        } = this.state;

        if (isLoading) {
            return (
                <Container className="py-5 text-center">
                    <Spinner color="primary" />
                </Container>
            );
        }
        if (notFound) {
            return (
                <Container className="py-5">
                    <h3>Song not found.</h3>
                    <Button color="secondary" onClick={() => this.props.navigate("/admin/songs")}>
                        Back to list
                    </Button>
                </Container>
            );
        }

        // Prepare select options
        const songBookOptions = allSongBooks.map(sb => ({
            value: sb.id,
            label: sb.shortTitle ? `${sb.title} (${sb.shortTitle})` : sb.title
        }));
        const themeOptions = allThemes.map(th => ({
            value: th.id,
            label: th.title
        }));

        // Merge preview arrays
        const alreadySongBooks = mergePreview.alreadySongBooks || [];
        const previewSongBooks = mergePreview.songBooks || [];
        const alreadyThemes = mergePreview.alreadyThemes || [];
        const previewThemes = mergePreview.themes || [];
        const alreadyRefs = mergePreview.alreadyReferences || [];
        const previewRefs = mergePreview.references || [];

        return (
            <>
                <SeoNoIndex noindex={true}/>
                {/* Header Section */}
                <section className="section section-lg section-shaped pb-10">
                    <div className="shape shape-style-1 shape-default">
                        <span/><span/><span/><span/><span/><span/><span/><span/><span/>
                    </div>
                    <Container className="py-lg-md d-flex">
                        <div className="col px-0">
                            <Row className="align-items-center">
                                <Col lg="8">
                                    <h1 className="display-3 text-white d-flex align-items-center">
                                        Edit Song &nbsp;
                                        <span id="songTitleText">{title}</span>
                                        &nbsp;
                                        <Button
                                            color="link"
                                            id="copyTitleBtn"
                                            onClick={this.handleCopyTitle}
                                            className="p-0 text-white"
                                            title="Copy title"
                                        >
                                            <FiCopy size={20} />
                                        </Button>
                                        <Tooltip
                                            placement="top"
                                            isOpen={copyTooltipOpen}
                                            target="copyTitleBtn"
                                        >
                                            Copied!
                                        </Tooltip>
                                    </h1>
                                </Col>
                                <Col lg="4" className="text-right">
                                    {/* Optional header actions */}
                                </Col>
                            </Row>
                        </div>
                    </Container>
                    <div className="separator separator-bottom separator-skew">
                        <svg xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 2560 100">
                            <polygon className="fill-white" points="2560 0 2560 100 0 100"/>
                        </svg>
                    </div>
                </section>

                <Container fluid className="pb-5">
                    <Row className="justify-content-center">
                        <Col xl="10" lg="12">
                            <Card className="shadow border-0">
                                <CardBody className="py-4">
                                    {/* Back to list */}
                                    <Button color="link" onClick={() => this.props.navigate("/admin/songs")} className="mb-3 p-0">
                                        &larr; Back to list
                                    </Button>

                                    <div className="d-flex align-items-center justify-content-between mb-4">
                                        <h2 className="mb-0">Edit: {title}</h2>
                                        <div>
                                            <Button
                                                color="secondary"
                                                onClick={this.handleCancel}
                                                type="button"
                                                className="mr-2"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                color="danger"
                                                onClick={this.handleDelete}
                                                type="button"
                                                disabled={isDeleting}
                                                className="mr-3"
                                            >
                                                {isDeleting ? "Deleting..." : "Delete"}
                                            </Button>
                                            <Button color="primary" type="submit" form="song-detail-form" disabled={isSaving}>
                                                {isSaving ? "Saving..." : "Save"}
                                            </Button>
                                        </div>
                                    </div>

                                    <Form id="song-detail-form" onSubmit={this.handleSave}>
                                        {/* ID and Title */}
                                        <Row form>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label for="id">Worship Bible ID</Label>
                                                    <Input id="id" name="id" type="text" value={id} readOnly/>
                                                </FormGroup>
                                            </Col>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label for="title">Title</Label>
                                                    <Input
                                                        id="title"
                                                        name="title"
                                                        type="text"
                                                        value={title}
                                                        onChange={e => this.setState({title: e.target.value})}
                                                        required
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>
                                        <Row form>
                                            <Col md="6">
                                                <FormGroup>
                                                    <Label for="alternativeTitle">Alternative Title</Label>
                                                    <Input
                                                        id="alternativeTitle"
                                                        name="alternativeTitle"
                                                        type="text"
                                                        value={alternativeTitle}
                                                        onChange={e => this.setState({alternativeTitle: e.target.value})}
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        <FormGroup>
                                            <Label>Currently Associated Authors</Label>
                                            {associatedAuthorIds.length > 0 ? (
                                                <ListGroup className="mb-3">
                                                    {allAuthors
                                                        .filter(a => associatedAuthorIds.includes(a.id))
                                                        .map(a => (
                                                            <ListGroupItem key={a.id}>{a.name}</ListGroupItem>
                                                        ))
                                                    }
                                                </ListGroup>
                                            ) : (
                                                <div className="text-muted mb-3">No associated authors.</div>
                                            )}
                                        </FormGroup>

                                        <FormGroup>
                                            <Label>Edit Associated Authors</Label>
                                            <Select
                                                isMulti
                                                options={allAuthors
                                                    .slice()
                                                    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? ""))
                                                    .map(a => ({value: a.id, label: a.name ?? ""}))}
                                                value={allAuthors
                                                    .filter(a => associatedAuthorIds.includes(a.id))
                                                    .map(a => ({value: a.id, label: a.name ?? ""}))}
                                                onChange={this.handleAuthorsSelectChange}
                                                placeholder="Select authors…"
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                            />
                                        </FormGroup>

                                        {/* Associations: SongBooks */}
                                        <Row>
                                            <Col lg="6" className="border-right">
                                                <FormGroup>
                                                    <Label>Currently Associated Song Books</Label>
                                                    {associatedSongBooks.length > 0 ? (
                                                        <div className="mb-3">
                                                            {associatedSongBooks.map((sb, idx) => (
                                                                <div key={sb.id}
                                                                     className="d-flex align-items-center mb-2">
                                                                    <div style={{flex: 1}}>
                                                                        <strong>{sb.title}</strong>
                                                                        {sb.shortTitle && <> ({sb.shortTitle})</>}
                                                                        <Button
                                                                            color="link"
                                                                            size="sm"
                                                                            className="p-0 ml-2"
                                                                            title="Copy title"
                                                                            onClick={() => navigator.clipboard.writeText(sb.title)}
                                                                        >
                                                                            <FiCopy size={14} />
                                                                        </Button>
                                                                    </div>
                                                                    <Input
                                                                        type="text"
                                                                        value={sb.songBookSongNumber}
                                                                        placeholder="Song number"
                                                                        style={{width: "120px", marginLeft: "1rem"}}
                                                                        onChange={e => {
                                                                            const val = e.target.value;
                                                                            this.setState(prev => {
                                                                                const arr = prev.associatedSongBooks.map((item, i) =>
                                                                                    i === idx ? {
                                                                                        ...item,
                                                                                        songBookSongNumber: val
                                                                                    } : item
                                                                                );
                                                                                return {associatedSongBooks: arr};
                                                                            });
                                                                        }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div className="text-muted mb-3">No associated song books.</div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>Edit Associated Song Books</Label>
                                                    <Select
                                                        isMulti
                                                        options={songBookOptions}
                                                        value={songBookOptions.filter(opt => associatedSbIds.includes(opt.value))}
                                                        onChange={this.handleSongBooksSelectChange}
                                                        placeholder="Select song books..."
                                                        className="react-select-container mb-3"
                                                        classNamePrefix="react-select"
                                                    />
                                                </FormGroup>
                                            </Col>

                                            {/* Themes */}
                                            <Col lg="6">
                                                <FormGroup>
                                                    <Label>Currently Associated Themes</Label>
                                                    {associatedThemeIds.length > 0 ? (
                                                        <ListGroup className="mb-3">
                                                            {allThemes
                                                                .filter(th => associatedThemeIds.includes(th.id))
                                                                .map(th => (
                                                                    <ListGroupItem
                                                                        key={th.id}
                                                                        action
                                                                        onClick={() => this.props.navigate(`/admin/theme/${th.id}`)}
                                                                        style={{ cursor: "pointer" }}
                                                                    >
                                                                        {th.title}
                                                                    </ListGroupItem>
                                                                ))
                                                            }
                                                        </ListGroup>
                                                    ) : (
                                                        <div className="text-muted mb-3">No associated themes.</div>
                                                    )}
                                                </FormGroup>
                                                <FormGroup>
                                                    <Label>Edit Associated Themes</Label>
                                                    <Select
                                                        isMulti
                                                        options={themeOptions}
                                                        value={themeOptions.filter(opt => associatedThemeIds.includes(opt.value))}
                                                        onChange={this.handleThemesSelectChange}
                                                        placeholder="Select themes..."
                                                        className="react-select-container mb-3"
                                                        classNamePrefix="react-select"
                                                    />
                                                </FormGroup>
                                            </Col>
                                        </Row>

                                        {/* Scripture References */}
                                        <Card className="mt-4 border-info">
                                            <CardBody>
                                                <h4 className="mb-3">Scripture References</h4>
                                                <Table responsive bordered hover className="mb-2">
                                                    <thead className="thead-light">
                                                    <tr>
                                                        <th>Book</th>
                                                        <th style={{width: "10%"}}>Chapter</th>
                                                        <th style={{width: "10%"}}>Verse From</th>
                                                        <th style={{width: "10%"}}>Verse To</th>
                                                        <th style={{width: "5%"}}></th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    {references.length > 0 ? references.map((ref, idx) => {
                                                        const bookInfo = allBooks.find(b => b.id === ref.bookId);
                                                        const chapterCount = bookInfo ? bookInfo.chapterCount : 0;
                                                        const versesCountMap = bookInfo ? bookInfo.versesCount || {} : {};
                                                        const chapterOptions = [];
                                                        for (let c = 1; c <= (chapterCount || 0); c++) chapterOptions.push(c);
                                                        const verseCount = ref.chapter && versesCountMap[ref.chapter] ? versesCountMap[ref.chapter] : 0;
                                                        const verseOptions = [];
                                                        for (let v = 1; v <= verseCount; v++) verseOptions.push(v);

                                                        return (
                                                            <tr key={idx}>
                                                                <td>
                                                                    <Input
                                                                        type="select"
                                                                        value={ref.bookId}
                                                                        onChange={e => this.handleReferenceChange(idx, "bookId", e.target.value)}
                                                                    >
                                                                        <option value="">Select book</option>
                                                                        {allBooks.map(bk => (
                                                                            <option key={bk.id} value={bk.id}>
                                                                                {bk.title}
                                                                            </option>
                                                                        ))}
                                                                    </Input>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="select"
                                                                        value={ref.chapter || ""}
                                                                        disabled={!ref.bookId}
                                                                        onChange={e => this.handleReferenceChange(idx, "chapter", e.target.value)}
                                                                    >
                                                                        <option value="">-</option>
                                                                        {chapterOptions.map(c => (
                                                                            <option key={c} value={c}>{c}</option>
                                                                        ))}
                                                                    </Input>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="select"
                                                                        value={ref.verseFrom || ""}
                                                                        disabled={!ref.chapter}
                                                                        onChange={e => this.handleReferenceChange(idx, "verseFrom", e.target.value)}
                                                                    >
                                                                        <option value="">-</option>
                                                                        {verseOptions.map(v => (
                                                                            <option key={v} value={v}>{v}</option>
                                                                        ))}
                                                                    </Input>
                                                                </td>
                                                                <td>
                                                                    <Input
                                                                        type="select"
                                                                        value={ref.verseTo || ""}
                                                                        disabled={!ref.verseFrom}
                                                                        onChange={e => this.handleReferenceChange(idx, "verseTo", e.target.value)}
                                                                    >
                                                                        <option value="">-</option>
                                                                        {verseOptions
                                                                            .filter(v => !ref.verseFrom || v >= Number(ref.verseFrom))
                                                                            .map(v => (
                                                                                <option key={v} value={v}>{v}</option>
                                                                            ))
                                                                        }
                                                                    </Input>
                                                                </td>
                                                                <td className="text-center">
                                                                    <Button
                                                                        color="link"
                                                                        className="p-0 text-danger"
                                                                        onClick={() => this.handleRemoveReference(idx)}
                                                                        title="Remove"
                                                                    >
                                                                        <RiDeleteBinLine size={18}/>
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        );
                                                    }) : (
                                                        <tr>
                                                            <td colSpan="5" className="text-center text-muted">
                                                                No references added.
                                                            </td>
                                                        </tr>
                                                    )}
                                                    </tbody>
                                                </Table>
                                                <Button color="secondary" size="sm" onClick={this.handleAddReference}>
                                                    + Add Reference
                                                </Button>
                                            </CardBody>
                                        </Card>

                                        {/* Musical Metadata */}
                                        {associatedSongBooks.length > 0 && (
                                            <Card className="mt-4 border-secondary">
                                                <CardBody>
                                                    <h4 className="mb-3">Musical Metadata</h4>
                                                    {metadataSaveError && (
                                                        <div className="alert alert-danger">{metadataSaveError}</div>
                                                    )}
                                                    {associatedSongBooks.map(sb => {
                                                        const wsbId = sb.wsbId;
                                                        if (!wsbId) return null;
                                                        const meta = songBookMetadata[wsbId] || {};
                                                        const isSaving = savingMetadataId === wsbId;
                                                        const saveSuccess = metadataSaveSuccessId === wsbId;
                                                        return (
                                                            <div key={wsbId} className="mb-4">
                                                                <h6 className="text-muted mb-2">
                                                                    {sb.title}{sb.shortTitle ? ` (${sb.shortTitle})` : ""} &mdash; #{sb.songBookSongNumber}
                                                                </h6>
                                                                <Row form>
                                                                    <Col md="3">
                                                                        <FormGroup>
                                                                            <Label>Key Signature</Label>
                                                                            <Input
                                                                                type="text"
                                                                                placeholder="e.g. D"
                                                                                value={meta.keySignature || ""}
                                                                                onChange={e => this.handleMetadataChange(wsbId, "keySignature", e.target.value)}
                                                                            />
                                                                        </FormGroup>
                                                                    </Col>
                                                                    <Col md="3">
                                                                        <FormGroup>
                                                                            <Label>Time Signature</Label>
                                                                            <Input
                                                                                type="text"
                                                                                placeholder="e.g. 4/4"
                                                                                value={meta.timeSignature || ""}
                                                                                onChange={e => this.handleMetadataChange(wsbId, "timeSignature", e.target.value)}
                                                                            />
                                                                        </FormGroup>
                                                                    </Col>
                                                                    <Col md="2">
                                                                        <FormGroup>
                                                                            <Label>Tempo (BPM)</Label>
                                                                            <Input
                                                                                type="number"
                                                                                placeholder="e.g. 72"
                                                                                min="0"
                                                                                value={meta.tempoBpm !== undefined ? meta.tempoBpm : ""}
                                                                                onChange={e => this.handleMetadataChange(wsbId, "tempoBpm", e.target.value)}
                                                                            />
                                                                        </FormGroup>
                                                                    </Col>
                                                                    <Col md="2">
                                                                        <FormGroup>
                                                                            <Label>Meter</Label>
                                                                            <Input
                                                                                type="text"
                                                                                placeholder="e.g. 8.7.8.7"
                                                                                value={meta.meter || ""}
                                                                                onChange={e => this.handleMetadataChange(wsbId, "meter", e.target.value)}
                                                                            />
                                                                        </FormGroup>
                                                                    </Col>
                                                                    <Col md="2">
                                                                        <FormGroup>
                                                                            <Label>CCLI Number</Label>
                                                                            <Input
                                                                                type="text"
                                                                                placeholder="e.g. 1234567"
                                                                                value={meta.ccliNumber || ""}
                                                                                onChange={e => this.handleMetadataChange(wsbId, "ccliNumber", e.target.value)}
                                                                            />
                                                                        </FormGroup>
                                                                    </Col>
                                                                </Row>
                                                                <Button
                                                                    color="secondary"
                                                                    size="sm"
                                                                    onClick={() => this.handleSaveMetadata(wsbId)}
                                                                    disabled={isSaving}
                                                                >
                                                                    {isSaving ? "Saving…" : "Save Metadata"}
                                                                </Button>
                                                                {saveSuccess && (
                                                                    <span className="text-success ml-2 small">Saved</span>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </CardBody>
                                            </Card>
                                        )}

                                        {/* Actions */}
                                        <div className="d-flex align-items-center justify-content-between mb-4 mt-4">
                                            <h2 className="mb-0"></h2>
                                            <div>
                                                <Button
                                                    color="secondary"
                                                    onClick={this.handleCancel}
                                                    type="button"
                                                    className="mr-2"
                                                >
                                                    Cancel
                                                </Button>
                                                <Button
                                                    color="danger"
                                                    onClick={this.handleDelete}
                                                    type="button"
                                                    disabled={isDeleting}
                                                    className="mr-3"
                                                >
                                                    {isDeleting ? "Deleting..." : "Delete"}
                                                </Button>
                                                <Button color="primary" type="submit" disabled={isSaving}>
                                                    {isSaving ? "Saving..." : "Save"}
                                                </Button>
                                            </div>
                                        </div>

                                        {/* Copy Scripture References */}
                                        <Card className="mt-4 border-secondary">
                                            <CardBody>
                                                <h4 className="mb-2">Copy Scripture References to another song</h4>
                                                <p className="text-muted small mb-3">
                                                    Copy all scripture references from <strong>{title}</strong> to another song. This song keeps its references. Duplicates are skipped.
                                                </p>

                                                {copyRefSuccess && <Alert color="success">{copyRefSuccess}</Alert>}
                                                {copyRefError   && <Alert color="danger">{copyRefError}</Alert>}

                                                {!copyRefConfirming ? (
                                                    <>
                                                        <form onSubmit={this.handleCopyRefSearch} className="d-flex mb-2">
                                                            <Input
                                                                type="text"
                                                                placeholder="Search for target song by title…"
                                                                value={copyRefSearchQuery}
                                                                onChange={e => this.setState({ copyRefSearchQuery: e.target.value })}
                                                            />
                                                            <Button color="secondary" type="submit" className="ml-2">Search</Button>
                                                        </form>
                                                        {copyRefSearchResults.length > 0 && (
                                                            <ListGroup>
                                                                {copyRefSearchResults.map(s => (
                                                                    <ListGroupItem
                                                                        key={s.id}
                                                                        className="d-flex justify-content-between align-items-center"
                                                                        style={{ cursor: "pointer" }}
                                                                        onClick={() => this.handleSelectCopyRefTarget(s)}
                                                                    >
                                                                        <span>
                                                                            <strong>{s.title}</strong>
                                                                            {s.alternativeTitle && <> <em>({s.alternativeTitle})</em></>}
                                                                        </span>
                                                                        <span className="text-muted small ml-2">{s.id}</span>
                                                                    </ListGroupItem>
                                                                ))}
                                                            </ListGroup>
                                                        )}
                                                    </>
                                                ) : (
                                                    <Alert color="warning">
                                                        <p className="mb-3">
                                                            <strong>Are you sure?</strong>{' '}
                                                            {references.length} reference{references.length !== 1 ? "s" : ""} from{' '}
                                                            <strong>{title}</strong> will be copied to{' '}
                                                            <strong>{copyRefTarget.title}</strong>. Duplicates will be skipped.
                                                        </p>
                                                        <Button
                                                            type="button"
                                                            color="danger"
                                                            onClick={this.handleConfirmCopyRef}
                                                            disabled={copyingRefs}
                                                            className="mr-2"
                                                        >
                                                            {copyingRefs ? "Copying…" : `Confirm — copy to "${copyRefTarget.title}"`}
                                                        </Button>
                                                        <Button
                                                            type="button"
                                                            color="secondary"
                                                            onClick={this.handleCancelCopyRef}
                                                            disabled={copyingRefs}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </Alert>
                                                )}
                                            </CardBody>
                                        </Card>

                                        {/* Merge from another song */}
                                        <Card className="mt-4 border-warning">
                                            <CardBody>
                                                <h4 className="mb-3">Merge from another song</h4>
                                                <p className="text-muted small mb-3">
                                                    Copy songbook associations, themes, and scripture references from another song into this one. The source song is not modified or deleted.
                                                </p>
                                                <FormGroup>
                                                    <Label for="mergeSource">Select source song</Label>
                                                    <AsyncSelect
                                                        cacheOptions
                                                        loadOptions={this.loadMergeOptions}
                                                        defaultOptions={false}
                                                        value={mergeSource}
                                                        inputValue={mergeSearchInput}
                                                        onInputChange={(value, { action }) => {
                                                            if (action === 'input-change') {
                                                                this.setState({ mergeSearchInput: value });
                                                            }
                                                        }}
                                                        onChange={option => {
                                                            if (option) {
                                                                this.setState({ mergeSource: option, mergeSearchInput: option.label });
                                                            } else {
                                                                this.setState({ mergeSource: null, mergeSearchInput: "" });
                                                            }
                                                        }}
                                                        placeholder="Type to search songs…"
                                                        isClearable
                                                    />
                                                </FormGroup>
                                                <Button
                                                    type="button"
                                                    color="warning"
                                                    disabled={!mergeSource}
                                                    onClick={this.handlePreviewMerge}
                                                >
                                                    Preview Merge
                                                </Button>
                                            </CardBody>
                                        </Card>
                                    </Form>

                                    {/* Merge Confirmation Modal */}
                                    <Modal
                                        isOpen={showMergeModal}
                                        toggle={() => this.setState({showMergeModal: false})}
                                        size="lg"
                                    >
                                        <ModalHeader toggle={() => this.setState({showMergeModal: false})}>
                                            Confirm Merge from “{mergeSource?.label}”
                                        </ModalHeader>
                                        <ModalBody>
                                            {/* Song Books */}
                                            <h5>Song Books in source:</h5>
                                            {alreadySongBooks.length > 0 && (
                                                <>
                                                    <div><strong>Already associated:</strong></div>
                                                    <ListGroup className="mb-2">
                                                        {alreadySongBooks.map(sb => (
                                                            <ListGroupItem key={sb.songBookId}>
                                                                <strong>{sb.title}</strong>{sb.shortTitle && <> ({sb.shortTitle})</>} &nbsp;
                                                                (#{sb.songBookSongNumber})
                                                            </ListGroupItem>
                                                        ))}
                                                    </ListGroup>
                                                </>
                                            )}
                                            {previewSongBooks.length > 0 ? (
                                                <>
                                                    <div><strong>Will be added:</strong></div>
                                                    <ListGroup className="mb-3">
                                                        {previewSongBooks.map(sb => (
                                                            <ListGroupItem key={sb.songBookId}>
                                                                <strong>{sb.title}</strong>{sb.shortTitle && <> ({sb.shortTitle})</>} &nbsp;
                                                                (#{sb.songBookSongNumber})
                                                            </ListGroupItem>
                                                        ))}
                                                    </ListGroup>
                                                </>
                                            ) : (
                                                alreadySongBooks.length === 0
                                                    ? <p className="text-muted">No song books in source.</p>
                                                    : <p className="text-muted">No new song books to add.</p>
                                            )}

                                            {/* Themes */}
                                            <h5>Themes in source:</h5>
                                            {alreadyThemes.length > 0 && (
                                                <>
                                                    <div><strong>Already associated:</strong></div>
                                                    <ListGroup className="mb-2">
                                                        {alreadyThemes.map(th => (
                                                            <ListGroupItem key={th.id}>{th.title}</ListGroupItem>
                                                        ))}
                                                    </ListGroup>
                                                </>
                                            )}
                                            {previewThemes.length > 0 ? (
                                                <>
                                                    <div><strong>Will be added:</strong></div>
                                                    <ListGroup className="mb-3">
                                                        {previewThemes.map(th => (
                                                            <ListGroupItem key={th.id}>{th.title}</ListGroupItem>
                                                        ))}
                                                    </ListGroup>
                                                </>
                                            ) : (
                                                alreadyThemes.length === 0
                                                    ? <p className="text-muted">No themes in source.</p>
                                                    : <p className="text-muted">No new themes to add.</p>
                                            )}

                                            {/* References */}
                                            <h5>References in source:</h5>
                                            {alreadyRefs.length > 0 && (
                                                <>
                                                    <div><strong>Already associated:</strong></div>
                                                    <Table bordered size="sm" className="mb-2">
                                                        <thead><tr><th>Book</th><th>Chapter</th><th>From</th><th>To</th></tr></thead>
                                                        <tbody>
                                                        {alreadyRefs.map((r, idx) => {
                                                            const book = allBooks.find(b => b.id === r.bookId);
                                                            const btitle = book ? book.title : r.bookId;
                                                            return (
                                                                <tr key={"a"+idx}>
                                                                    <td>{btitle}</td>
                                                                    <td>{r.chapter}</td>
                                                                    <td>{r.verseFrom}</td>
                                                                    <td>{r.verseTo}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                        </tbody>
                                                    </Table>
                                                </>
                                            )}
                                            {previewRefs.length > 0 ? (
                                                <>
                                                    <div><strong>Will be added:</strong></div>
                                                    <Table bordered size="sm" className="mb-3">
                                                        <thead><tr><th>Book</th><th>Chapter</th><th>From</th><th>To</th></tr></thead>
                                                        <tbody>
                                                        {previewRefs.map((r, idx) => {
                                                            const book = allBooks.find(b => b.id === r.bookId);
                                                            const btitle = book ? book.title : r.bookId;
                                                            return (
                                                                <tr key={"n"+idx}>
                                                                    <td>{btitle}</td>
                                                                    <td>{r.chapter}</td>
                                                                    <td>{r.verseFrom}</td>
                                                                    <td>{r.verseTo}</td>
                                                                </tr>
                                                            );
                                                        })}
                                                        </tbody>
                                                    </Table>
                                                </>
                                            ) : (
                                                alreadyRefs.length === 0
                                                    ? <p className="text-muted">No references in source.</p>
                                                    : <p className="text-muted">No new references to add.</p>
                                            )}
                                        </ModalBody>
                                        <ModalFooter>
                                            <Button color="secondary" onClick={() => this.setState({ showMergeModal: false })}>
                                                Cancel
                                            </Button>
                                            <Button color="primary" onClick={this.handleApplyMerge} disabled={isMerging}>
                                                {isMerging ? "Merging..." : "Apply Merge"}
                                            </Button>
                                        </ModalFooter>
                                    </Modal>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }
}

// HOC to inject navigate and params into class component
function withRouterParamsAndNavigate(Component) {
    return function Wrapped(props) {
        const navigate = useNavigate();
        const params = useParams();
        return <Component {...props} navigate={navigate} params={params} />;
    };
}

export default withRouterParamsAndNavigate(AdminSongDetail);
